import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline'
import clsx from 'clsx'
import BackButton from '../ui/BackButton'
import { ALL_PARAMETERS, ParameterKey } from '../../utils/weldParameterAggregator'
import { getWeldParameterHeaderLabel } from '../../utils/weldParameterHeaderLabels'
import {
  AggKey,
  defaultVisibleColumns,
  VisibleColumns
} from '../../utils/weldParametersColumnLayout'
import { getParameterUnitSuffix } from '../../utils/weldUnitUtils'
import { useUnitSetting } from '../../utils/userUnitSetting'

const AGG_KEYS: AggKey[] = ['min', 'avg', 'max']
const AGG_LABELS: Record<AggKey, string> = { min: 'Min', avg: 'Avg', max: 'Max' }

const MIN_REQUIRED = 2

type CategoryId = 'basicInfo' | 'parameters'

interface ColumnSelectorDialogProps {
  open: boolean
  value: VisibleColumns
  onChange: (next: VisibleColumns) => void
  onClose: () => void
  /** Unit suffix for Preheat / Interpass (from pipe `preheatUnit`). */
  preheatUnit?: string
}

/**
 * Column selector for the Welding Parameters table.
 *
 * Layout:
 *   - "Basic Info" section: Torch (single toggle)
 *   - "Parameters" section: every parameter as an expandable parent
 *     with Min/Avg/Max sub-cells (the parent checkbox toggles all three).
 *
 * Behaviour:
 *   - Local state buffer — changes are only emitted on Save.
 *   - Cancel / Escape / close-button discards local edits.
 *   - Save is gated behind a minimum of 2 toggled columns total.
 */
export default function ColumnSelectorDialog({
  open,
  value,
  onChange,
  onClose,
  preheatUnit
}: ColumnSelectorDialogProps) {
  useUnitSetting()
  const unitProbe = getParameterUnitSuffix('travelSpeed')
  const parameterLabels = useMemo(() => {
    const labels = {} as Record<ParameterKey, string>
    for (const param of ALL_PARAMETERS) {
      labels[param] = getWeldParameterHeaderLabel(param, preheatUnit)
    }
    return labels
  }, [preheatUnit, unitProbe])

  const [draft, setDraft] = useState<VisibleColumns>(value)
  const [searchQuery, setSearchQuery] = useState('')
  const [expanded, setExpanded] = useState<Set<ParameterKey>>(new Set())

  useEffect(() => {
    if (!open) return
    setDraft(cloneVisible(value))
    setSearchQuery('')
    setExpanded(new Set())
  }, [open, value])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const visibleParameters = useMemo(
    () => filterParameters(ALL_PARAMETERS, searchQuery, parameterLabels),
    [searchQuery, parameterLabels]
  )

  const torchMatches = useMemo(
    () => matchesQuery('Torch', searchQuery) || matchesQuery('Basic Info', searchQuery),
    [searchQuery]
  )

  const filteredKeys = useMemo(
    () => collectKeys(visibleParameters, torchMatches),
    [visibleParameters, torchMatches]
  )

  const allKeys = useMemo(() => collectKeys(ALL_PARAMETERS, true), [])

  const selectedCount = useMemo(() => countSelected(draft, allKeys), [draft, allKeys])
  const totalCount = allKeys.length

  const filteredSelectedCount = useMemo(
    () => countSelected(draft, filteredKeys),
    [draft, filteredKeys]
  )
  const filteredAllSelected =
    filteredKeys.length > 0 && filteredSelectedCount === filteredKeys.length

  const canSave = selectedCount >= MIN_REQUIRED

  if (!open) return null

  const toggleParamCell = (param: ParameterKey, agg: AggKey) => {
    setDraft((prev) => {
      const current = prev[param] ?? { min: false, avg: false, max: false }
      return {
        ...prev,
        [param]: { ...current, [agg]: !current[agg] }
      }
    })
  }

  const toggleParamParent = (param: ParameterKey) => {
    setDraft((prev) => {
      const current = prev[param] ?? { min: false, avg: false, max: false }
      const allOn = current.min && current.avg && current.max
      const next: VisibleColumns = {
        ...prev,
        [param]: allOn
          ? { min: false, avg: false, max: false }
          : { min: true, avg: true, max: true }
      }
      return next
    })
    setExpanded((prev) => {
      if (prev.has(param)) return prev
      const next = new Set(prev)
      next.add(param)
      return next
    })
  }

  const toggleTorch = () => {
    setDraft((prev) => ({ ...prev, torch: !prev.torch }))
  }

  const toggleExpanded = (param: ParameterKey) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(param)) next.delete(param)
      else next.add(param)
      return next
    })
  }

  const handleSelectAll = () => {
    setDraft((prev) => {
      if (filteredAllSelected) {
        return setKeys(prev, filteredKeys, false)
      }
      return setKeys(prev, filteredKeys, true)
    })
  }

  const handleReset = () => {
    setDraft(defaultVisibleColumns())
    setExpanded(new Set())
  }

  const handleSave = () => {
    if (!canSave) return
    onChange(cloneVisible(draft))
    onClose()
  }

  const handleCancel = () => {
    onClose()
  }

  const content = (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="column-selector-title"
    >
      <div className="relative flex h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl">
        {/* Header */}
        <div className="flex flex-shrink-0 items-center gap-3 border-b border-[#E2E5EA] px-5 py-4">
          <BackButton onClick={handleCancel} tooltip="Close" />
          <h2
            id="column-selector-title"
            className="font-heading text-base font-semibold text-blueprint-blue"
          >
            Select Columns
          </h2>
        </div>

        {/* Body */}
        <div className="flex flex-1 min-h-0 flex-col gap-3 px-5 py-4">
          {/* Search + controls */}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                data-testid="col-search"
                placeholder="Search columns..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-md border border-[#E2E5EA] bg-white py-2 pl-9 pr-3 text-sm text-text-grey placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
              />
            </div>
            <div className="flex flex-shrink-0 gap-2">
              <button
                type="button"
                data-testid="col-select-all"
                onClick={handleSelectAll}
                className="inline-flex items-center gap-1.5 rounded-md border border-[#E2E5EA] bg-white px-3 py-1.5 text-xs font-semibold text-heritage-grey shadow-sm transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
              >
                {filteredAllSelected ? 'Deselect All' : 'Select All'}
              </button>
              <button
                type="button"
                data-testid="col-reset"
                onClick={handleReset}
                className="inline-flex items-center gap-1.5 rounded-md border border-[#E2E5EA] bg-white px-3 py-1.5 text-xs font-semibold text-heritage-grey shadow-sm transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
              >
                <ArrowPathIcon className="h-3.5 w-3.5" />
                Reset to default
              </button>
            </div>
          </div>

          {/* Selected count */}
          <div className="flex flex-shrink-0 items-center gap-2 rounded-md border border-blue-100 bg-blue-50/70 px-3 py-2 text-xs text-text-grey">
            <span className="font-semibold">
              {selectedCount} of {totalCount} columns selected
            </span>
            {selectedCount < MIN_REQUIRED ? (
              <span className="font-semibold text-error-600">
                (Minimum {MIN_REQUIRED} columns required)
              </span>
            ) : null}
          </div>

          {/* Categories list */}
          <div className="flex-1 min-h-0 overflow-y-auto rounded-md border border-[#E2E5EA] bg-page-bg p-4">
            {!torchMatches && visibleParameters.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center text-center text-sm text-heritage-grey">
                <p className="font-medium">No columns match &ldquo;{searchQuery}&rdquo;</p>
                <p className="mt-1 text-xs">Try a different search term.</p>
              </div>
            ) : (
              <div className="grid gap-4 lg:grid-cols-1">
                {/* {torchMatches ? (
                  <CategoryCard category="basicInfo" title="Basic Info">
                    <div
                      className={clsx(
                        'flex items-center gap-2 rounded-md border bg-white px-3 py-2.5 transition-all',
                        draft.torch
                          ? 'border-primary-200 bg-primary-50/40 shadow-sm'
                          : 'border-[#E2E5EA] hover:border-[#C9CDD4]'
                      )}
                    >
                      <span className="w-5" aria-hidden />
                      <input
                        type="checkbox"
                        id="col-cb-torch"
                        data-testid="col-cb-torch"
                        checked={Boolean(draft.torch)}
                        onChange={toggleTorch}
                        className="h-4 w-4 flex-shrink-0 cursor-pointer rounded border-heritage-grey text-hero-red focus:outline-none focus:ring-2 focus:ring-hero-red/40"
                      />
                      <label
                        htmlFor="col-cb-torch"
                        className="flex-1 cursor-pointer select-none text-sm font-medium text-text-grey"
                      >
                        Torch
                      </label>
                    </div>
                  </CategoryCard>
                ) : null} */}

                {visibleParameters.length > 0 ? (
                  <CategoryCard
                    category="parameters"
                    title="Parameters"
                    className={clsx(torchMatches ? '' : 'lg:col-span-2')}
                  >
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                      {visibleParameters.map((param) => (
                        <ParameterRow
                          key={param}
                          param={param}
                          label={parameterLabels[param]}
                          flags={
                            draft[param] ?? { min: false, avg: false, max: false }
                          }
                          expanded={expanded.has(param)}
                          onToggleParent={() => toggleParamParent(param)}
                          onToggleExpanded={() => toggleExpanded(param)}
                          onToggleCell={(agg) => toggleParamCell(param, agg)}
                        />
                      ))}
                    </div>
                  </CategoryCard>
                ) : null}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-shrink-0 items-center justify-end gap-3 border-t border-[#E2E5EA] bg-white px-5 py-3">
          <button
            type="button"
            data-testid="col-cancel"
            onClick={handleCancel}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="col-save"
            onClick={handleSave}
            disabled={!canSave}
            className="btn-primary inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  )

  return createPortal(content, document.body)
}

interface CategoryCardProps {
  category: CategoryId
  title: string
  className?: string
  children: React.ReactNode
}

function CategoryCard({ category, title, className, children }: CategoryCardProps) {
  return (
    <div
      data-testid={`col-category-${category}`}
      className={clsx(
        'flex flex-col rounded-lg border border-[#E2E5EA] bg-white p-4 shadow-sm',
        className
      )}
    >
      <h3 className="mb-3 flex-shrink-0 border-b-2 border-hero-red pb-2 text-sm font-bold uppercase tracking-wide text-blueprint-blue">
        {title}
      </h3>
      <div className="flex-1">{children}</div>
    </div>
  )
}

interface ParameterRowProps {
  param: ParameterKey
  label: string
  flags: { min: boolean; avg: boolean; max: boolean }
  expanded: boolean
  onToggleParent: () => void
  onToggleExpanded: () => void
  onToggleCell: (agg: AggKey) => void
}

function ParameterRow({
  param,
  label,
  flags,
  expanded,
  onToggleParent,
  onToggleExpanded,
  onToggleCell
}: ParameterRowProps) {
  const allOn = flags.min && flags.avg && flags.max
  const anyOn = flags.min || flags.avg || flags.max
  const indeterminate = anyOn && !allOn

  return (
    <div data-testid={`col-row-${param}`} className="space-y-1">
      <div
        className={clsx(
          'flex items-center gap-2 rounded-md border px-3 py-2 transition-all',
          anyOn
            ? 'border-primary-200 bg-primary-50/40 shadow-sm'
            : 'border-[#E2E5EA] bg-white hover:border-[#C9CDD4]'
        )}
      >
        <button
          type="button"
          aria-label={expanded ? 'Collapse' : 'Expand'}
          data-testid={`col-expand-${param}`}
          onClick={onToggleExpanded}
          className="inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-700"
        >
          {expanded ? (
            <ChevronDownIcon className="h-4 w-4" />
          ) : (
            <ChevronRightIcon className="h-4 w-4" />
          )}
        </button>
        <input
          type="checkbox"
          id={`col-cb-${param}-parent`}
          data-testid={`col-cb-${param}-parent`}
          checked={allOn}
          ref={(el) => {
            if (el) el.indeterminate = indeterminate
          }}
          onChange={onToggleParent}
          className="h-4 w-4 flex-shrink-0 cursor-pointer rounded border-heritage-grey text-hero-red focus:outline-none focus:ring-2 focus:ring-hero-red/40"
        />
        <label
          htmlFor={`col-cb-${param}-parent`}
          className="flex-1 cursor-pointer select-none text-sm font-medium text-text-grey"
        >
          {label}
        </label>
      </div>

      {expanded ? (
        <div className="ml-9 space-y-1.5">
          {AGG_KEYS.map((agg) => {
            const checked = flags[agg]
            const id = `col-cb-${param}-${agg}`
            return (
              <div
                key={agg}
                className={clsx(
                  'flex items-center gap-2 rounded-md border px-2.5 py-1.5 transition-all',
                  checked
                    ? 'border-primary-200 bg-primary-50/40'
                    : 'border-[#E2E5EA] bg-white hover:border-[#C9CDD4]'
                )}
              >
                <span className="w-4" aria-hidden />
                <input
                  type="checkbox"
                  id={id}
                  data-testid={id}
                  aria-label={`${label} ${AGG_LABELS[agg]}`}
                  checked={checked}
                  onChange={() => onToggleCell(agg)}
                  className="h-4 w-4 flex-shrink-0 cursor-pointer rounded border-heritage-grey text-hero-red focus:outline-none focus:ring-2 focus:ring-hero-red/40"
                />
                <label
                  htmlFor={id}
                  className="flex-1 cursor-pointer select-none text-xs font-medium text-text-grey"
                >
                  {AGG_LABELS[agg]}
                </label>
              </div>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}

function cloneVisible(value: VisibleColumns): VisibleColumns {
  const next = { torch: Boolean(value.torch) } as VisibleColumns
  for (const key of ALL_PARAMETERS) {
    const v = value[key] ?? { min: false, avg: false, max: false }
    next[key] = { min: v.min, avg: v.avg, max: v.max }
  }
  return next
}

function setKeys(value: VisibleColumns, keys: string[], on: boolean): VisibleColumns {
  const next = cloneVisible(value)
  for (const k of keys) {
    if (k === 'torch') {
      next.torch = on
      continue
    }
    const [param, agg] = k.split(':') as [ParameterKey, AggKey]
    const current = next[param] ?? { min: false, avg: false, max: false }
    next[param] = { ...current, [agg]: on }
  }
  return next
}

function countSelected(value: VisibleColumns, keys: string[]): number {
  let n = 0
  for (const k of keys) {
    if (k === 'torch') {
      if (value.torch) n++
      continue
    }
    const [param, agg] = k.split(':') as [ParameterKey, AggKey]
    const flags = value[param]
    if (flags && flags[agg]) n++
  }
  return n
}

function collectKeys(params: ParameterKey[], includeTorch: boolean): string[] {
  const keys: string[] = []
  if (includeTorch) keys.push('torch')
  for (const param of params) {
    for (const agg of AGG_KEYS) {
      keys.push(`${param}:${agg}`)
    }
  }
  return keys
}

function matchesQuery(text: string, query: string): boolean {
  if (!query.trim()) return true
  return text.toLowerCase().includes(query.trim().toLowerCase())
}

function filterParameters(
  params: ParameterKey[],
  query: string,
  labels: Record<ParameterKey, string>
): ParameterKey[] {
  const q = query.trim().toLowerCase()
  if (!q) return [...params]
  // If the query matches the category name or any sub-aggregation label,
  // show every parameter (so e.g. "min" reveals all parents).
  const categoryHit = 'parameters'.includes(q)
  const aggHit = AGG_KEYS.some((agg) => AGG_LABELS[agg].toLowerCase().includes(q))
  if (categoryHit || aggHit) return [...params]
  return params.filter((p) => {
    const label = labels[p].toLowerCase()
    return label.includes(q) || p.toLowerCase().includes(q)
  })
}
